import { objectKeys } from "app/helpers/objects";
import { CRM_RECORDS_DEFAULT_REQUEST } from "app/pages/sales-intelligence/modules/crm-integration/constants/dto";
import type { PaginatedTableRequest } from "app/pages/sales-intelligence/types/table";

import { COMPANY_DOMAINS_DEFAULT_REQUEST, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, ORDER_BY_MAP } from "../constants/dto";
import type { LeadsType } from "../types";
import type { ExpandedRowVariant } from "../types/expanded-row";

import type { ExpandedRowState, LeadsSearchState, TabLeadsSearchState } from "./types";

const TABS: readonly LeadsType[] = ["companies", "websites"];

export const getExpandedRowStateByVariant = (variant: ExpandedRowVariant): PaginatedTableRequest =>
    variant.kind === "crm-records" ? CRM_RECORDS_DEFAULT_REQUEST : COMPANY_DOMAINS_DEFAULT_REQUEST;

const createInitialTabState = (leadsType: LeadsType): TabLeadsSearchState => ({
    asc: false,
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy: ORDER_BY_MAP[leadsType],
});

export const createInitialExpandedRowState = (): ExpandedRowState => ({
    expandedRowKeys: [],
    expandedRowVariant: undefined,
    expandedRowState: {
        page: DEFAULT_PAGE,
        pageSize: 0,
    },
});

/**
 * Builds a fresh `tabs` map with every tab back on page 1.
 *
 * Must return a NEW object: `selectTabs` is a plain property selector feeding the memoized search-DTO
 * selectors, so mutating `tabsState` in place (the previous behaviour) left its reference unchanged and the
 * DTO kept the stale page — the store said page 1 while the request still asked for page N.
 */
export const resetTabsPaginationState = (tabsState: LeadsSearchState["tabs"]): LeadsSearchState["tabs"] =>
    objectKeys(tabsState).reduce<LeadsSearchState["tabs"]>(
        (state, id) => {
            state[id] = {
                ...tabsState[id],
                page: DEFAULT_PAGE,
            };

            return state;
        },
        {} as LeadsSearchState["tabs"],
    );

const initTabsState = (): LeadsSearchState["tabs"] =>
    TABS.reduce<LeadsSearchState["tabs"]>(
        (tabs, id) => {
            tabs[id] = createInitialTabState(id);
            return tabs;
        },
        {} as LeadsSearchState["tabs"],
    );

const initialLeadsSearchState: LeadsSearchState = {
    activeLeadsType: "companies",
    tabs: initTabsState(),

    isAggregatedDataUsed: true,
    columnNamesForHighlight: [],

    searchText: "",

    isShowSavedSearchListPanel: false,
    isRowSelectionEnabled: false,
    selectedRows: [],

    highlightedRowDomains: [],

    ...createInitialExpandedRowState(),
};

export default initialLeadsSearchState;
